import {
  AuthError,
  confirmResetPassword,
  confirmSignUp,
  deleteUser,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  SignUpInput,
  updatePassword,
  updateUserAttributes,
  UpdateUserAttributesInput,
} from 'aws-amplify/auth';
import posthog from 'posthog-js';

// --- supported AWS Cognito fields
// -> https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-attributes.html
// -> https://openid.net/specs/openid-connect-core-1_0.html#StandardClaims
export type RegisterParams = {
  userFirstName: string;
  userLastName: string;
  userPhone: string;
  userEmail: string;
  username: string;
  password: string;
  // optional
  locale?: string; // the language code, example: en-US, fr-CA, etc...
  address?: string;
  // custom
  city?: string;
  state?: string;
  zip?: string;
};

export type LoginParams = {
  username: string;
  password: string;
};

export type CodeParams = {
  username: string;
  code: string;
};

type UserParams = {
  name?: string;
  email?: string;
  address?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  phone_number?: string;
  given_name?: string;
  family_name?: string;
};

type ResetPasswordParams = {
  username: string;
  code: string;
  new_password: string;
};

type ChangePasswordParams = {
  oldPassword: string;
  newPassword: string;
};

const amplifyKeyPrefixes = ['amplify-', 'CognitoIdentityServiceProvider', 'aws.cognito'];

class auth {
  private _options: {};
  root: this;
  static user: any;

  constructor(options = {}) {
    this.root = this;
    this._options = options;
    auth.user = null;
  }

  static async register(params: RegisterParams) {
    const userData: SignUpInput = {
      username: params.username,
      password: params.password,
      options: {
        userAttributes: {
          email: params.userEmail,
          phone_number: params.userPhone,
          name: `${params.userFirstName} ${params.userLastName}`,
          given_name: params.userFirstName,
          family_name: params.userLastName,
        },
        autoSignIn: true,
      },
    };

    const { userId } = await signUp(userData);
    return userId;
  }

  static async login(params: LoginParams) {
    const { username, password } = params;

    let snapshot: any = null;
    let userAgent: any = null;
    if (typeof window !== 'undefined') {
      snapshot = this.getAmplifyStorageSnapshot();
      userAgent = navigator.userAgent;
    }
    posthog.capture('Login Attempt', {
      userId: username,
      appEnv: process.env.APP_ENV,
      userAgent,
      localStorageData: snapshot?.localStorage,
      sessionStorageData: snapshot?.sessionStorage,
      cookieData: snapshot?.cookies,
    });

    try {
      const user = await signIn({ username, password });

      if (!user.isSignedIn) {
        posthog.capture('Login Failure', {
          reason: 'User not signed in after signIn call',
        });
        throw new Error('User is not signed in');
      }

      return await this.getUser();
    } catch (error) {
      console.log('User login error: ', error);
      posthog.captureException(error, {
        reason: 'Login Failure',
      });
      throw error;
    }
  }

  static async signInWithGoogle() {
    await signInWithRedirect({ provider: 'Google' });
  }

  static async signInWithFacebook() {
    await signInWithRedirect({ provider: 'Facebook' });
  }

  static async signOut() {
    await signOut({ global: true });
    localStorage.clear();
    sessionStorage.clear();
    this.user = null;
  }

  static async resendConfirmationCode(params: Omit<CodeParams, 'code'>) {
    await resendSignUpCode({ username: params.username });
  }

  static async confirmSignUpCode(params: CodeParams) {
    await confirmSignUp({ username: params.username, confirmationCode: params.code });
  }

  static async deleteUser() {
    await this.ensureValidSession();
    const result = await deleteUser();
    await this.getUser();
    return result;
  }

  static async updateUser(params: UserParams) {
    const userAttributes: UpdateUserAttributesInput = {
      userAttributes: {
        name: params.name,
        address: params.address,
        phone_number: params.phone_number,
        given_name: params.given_name,
        family_name: params.family_name,
        'custom:country': params.country,
        'custom:state': params.state,
        'custom:city': params.city,
        'custom:zip': params.zip,
      },
    };
    try {
      await this.ensureValidSession();
      return await updateUserAttributes(userAttributes);
    } catch (error) {
      console.log('Update user attributes error: ', error);
      throw error;
    }
  }

  static async getUser() {
    try {
      const attributes = await fetchUserAttributes();
      this.user = await getCurrentUser();
      return { ...this.user, attributes };
    } catch (err) {
      return null; // If the user is not signed in, return null
    }
  }

  static async sendForgotPasswordCode(username: string) {
    return await resetPassword({ username });
  }

  static async resetPassword(params: ResetPasswordParams) {
    return await confirmResetPassword({
      username: params.username,
      confirmationCode: params.code,
      newPassword: params.new_password,
    });
  }

  static async changePassword(params: ChangePasswordParams) {
    try {
      await this.ensureValidSession();
      return await updatePassword({
        oldPassword: params.oldPassword,
        newPassword: params.newPassword,
      });
    } catch (error) {
      console.log('Change password error: ', error);
      throw error;
    }
  }

  static async ensureValidSession() {
    try {
      let session = await fetchAuthSession();
      // Force refresh only if the token is expired or about to expire
      if (session?.tokens?.idToken?.payload?.exp) {
        const expiry = new Date(session.tokens.idToken.payload.exp * 1000);
        if (expiry < new Date(Date.now() + 30000)) {
          // 30 second buffers
          session = await fetchAuthSession({ forceRefresh: true });
        }
      }

      if (!session?.tokens?.idToken) {
        throw new AuthError({
          name: 'UserNotSignedInException',
          message: 'User is not signed in.',
        });
      }
      return session;
    } catch (error) {
      if (
        error instanceof AuthError &&
        ['NotAuthorizedException', 'ExpiredTokenException', 'InvalidSessionException'].includes(
          error.name,
        )
      ) {
        await this.signOut();
      }
      throw error;
    }
  }

  private static getFilteredStorage(storage: Storage) {
    const result: Record<string, string> = {};

    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key) continue;

      if (amplifyKeyPrefixes.some(prefix => key.startsWith(prefix))) {
        const value = storage.getItem(key);
        if (value !== null) result[key] = value;
      }
    }

    return result;
  }

  private static getFilteredCookies(): Record<string, string> {
    if (typeof document === 'undefined') return {};

    return document.cookie
      .split('; ')
      .filter(cookie => amplifyKeyPrefixes.some(prefix => cookie.startsWith(prefix)))
      .reduce((acc: Record<string, string>, pair) => {
        const [key, val] = pair.split('=');
        if (key) acc[key] = decodeURIComponent(val || '');
        return acc;
      }, {});
  }

  private static getAmplifyStorageSnapshot() {
    if (typeof window === 'undefined') return null;

    return {
      localStorage: this.getFilteredStorage(window.localStorage),
      sessionStorage: this.getFilteredStorage(window.sessionStorage),
      cookies: this.getFilteredCookies(),
    };
  }
}

export default auth;
